import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import ApiService from "../../services/ApiService";
import { toast } from 'react-toastify';
import Loader from "../../components/Loader";
import { Dropdown } from 'primereact/dropdown';
import { MultiSelect } from 'primereact/multiselect';
import { getAppointmentStatus } from '../../utils/statusHelper';
import { calculateAge, formatCurrency, formatNumberWithCommas } from '../../utils/helper';
import AuthService from '../../services/AuthService';

const Checkup = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [specialists, setSpecialists] = useState([]);
    const [lensTypes, setLensTypes] = useState([]);
    const [appointment, setAppointment] = useState(null);
    const [lastCheckup, setLastCheckup] = useState(null);
    const [activeTab, setActiveTab] = useState('receptionist');
    const [isEditMode, setIsEditMode] = useState(true);
    const authUser = AuthService.getCurrentUser();

    const [receptionistData, setReceptionistData] = useState({
        checkup_type: 'Full Eye Checkup',
        ar_re: '', ar_le: '',
        rx_re_sph: '', rx_re_cyl: '', rx_re_axis: '', rx_re_va: '', rx_re_add: '',
        rx_le_sph: '', rx_le_cyl: '', rx_le_axis: '', rx_le_va: '', rx_le_add: '',
        pd_re: '', pd_le: '', pd_total: '',
        lensometer_re: '', lensometer_le: '',
        doctor_findings: '',
        doctor_medications: '',
        specialist_recommendation: '',
        next_visit: ''
    });

    const [doctorData, setDoctorData] = useState({
        checkup_type: 'Full Eye Checkup',
        ar_re: '', ar_le: '',
        rx_re_sph: '', rx_re_cyl: '', rx_re_axis: '', rx_re_va: '', rx_re_add: '',
        rx_le_sph: '', rx_le_cyl: '', rx_le_axis: '', rx_le_va: '', rx_le_add: '',
        pd_re: '', pd_le: '', pd_total: '',
        lensometer_re: '', lensometer_le: '',
        iop_re: '', iop_le: '',
        lens_types: [],
        doctor_findings: '',
        doctor_medications: '',
        specialist_recommendation: '',
        next_visit: ''
    });

    const [paymentMethod, setPaymentMethod] = useState('');
    const [amountPaid, setAmountPaid] = useState('');
    const [paymentStatus, setPaymentStatus] = useState('pending');

    const getLensTypes = async () => {
        try {
            const response = await ApiService.request({
                method: 'GET',
                url: 'admin/lens-types',
                data: { limit: 1000 }
            });
            if (response.data.status) {
                setLensTypes(response.data.data.lens_types);
            }
        } catch (error) {
            console.error(error);
        }
    };

    useEffect(() => {
        fetchData();
        getSpecialists();
        getLensTypes();
    }, [id]);

    useEffect(() => {
        if (activeTab === 'last_checkup') {
            setIsEditMode(false);
        } else if (activeTab === 'receptionist') {
            setIsEditMode(!appointment?.receptionist_records);
            // If receptionist starts checking (from queue or pending), update status to checkup_process (Under Pre-test)
            if (appointment && authUser.role === 'receptionist' && ['pending', 'queue'].includes(appointment.status)) {
                updateStatus('checkup_process');
            }
        } else if (activeTab === 'doctor') {
            setIsEditMode(!appointment?.doctor_records);
        }
    }, [activeTab, appointment]);

    const updateStatus = async (newStatus) => {
        try {
            const response = await ApiService.request({
                method: 'PUT',
                url: `admin/appointments/${id}`,
                data: { status: newStatus }
            });
            if (response.data.status) {
                // Update local appointment status without refetching everything to avoid loop
                setAppointment(prev => ({ ...prev, status: newStatus }));
            }
        } catch (error) {
            console.error("Failed to update status", error);
        }
    };

    const getSpecialists = async () => {
        try {
            const response = await ApiService.request({
                method: 'GET',
                url: 'admin/users',
                data: { role: 'specialist' }
            });
            if (response.data.status) {
                setSpecialists(response.data.data.users);
            }
        } catch (error) {
            console.error(error);
        }
    };

    const fetchData = async () => {
        setLoading(true);
        try {
            const response = await ApiService.request({
                method: 'GET',
                url: `admin/appointments/${id}`
            });

            if (response.data.status) {
                const appt = response.data.data;
                setAppointment(appt);
                if (appt.payment_method) {
                    setPaymentMethod(appt.payment_method);
                }
                if (appt.amount_paid !== undefined && appt.amount_paid !== null) {
                    setAmountPaid(appt.amount_paid);
                }
                if (appt.payment_status) {
                    setPaymentStatus(appt.payment_status);
                }
                if (appt.last_checkup) {
                    setLastCheckup(appt.last_checkup);
                }
                if (appt.receptionist_records) {
                    setReceptionistData(prev => ({ ...prev, ...appt.receptionist_records }));
                }
                if (appt.doctor_records) {
                    setDoctorData(prev => ({ ...prev, ...appt.doctor_records }));
                } else if (appt.receptionist_records) {
                    // Doctor hasn't recorded yet — prefill the doctor tab with the
                    // shared records the receptionist already captured (A/R, Present Rx, PD, Lensometer)
                    const sharedFields = [
                        'ar_re', 'ar_le',
                        'rx_re_sph', 'rx_re_cyl', 'rx_re_axis', 'rx_re_va', 'rx_re_add',
                        'rx_le_sph', 'rx_le_cyl', 'rx_le_axis', 'rx_le_va', 'rx_le_add',
                        'pd_re', 'pd_le', 'pd_total',
                        'lensometer_re', 'lensometer_le',
                    ];
                    setDoctorData(prev => {
                        const prefilled = { ...prev };
                        sharedFields.forEach(field => {
                            if (appt.receptionist_records[field] !== undefined && appt.receptionist_records[field] !== null) {
                                prefilled[field] = appt.receptionist_records[field];
                            }
                        });
                        return prefilled;
                    });
                }
            }
        } catch (error) {
            console.error(error);
            toast.error("Error fetching appointment data");
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        if (activeTab === 'receptionist') {
            setReceptionistData(prev => ({ ...prev, [name]: value }));
        } else {
            setDoctorData(prev => ({ ...prev, [name]: value }));
        }
    };

    const handleSpecialistChange = (e) => {
        if (activeTab === 'receptionist') {
            setReceptionistData(prev => ({ ...prev, specialist_recommendation: e.value }));
        } else {
            setDoctorData(prev => ({ ...prev, specialist_recommendation: e.value }));
        }
    };

    const calculateTotalPD = () => {
        const data = activeTab === 'receptionist' ? receptionistData : doctorData;
        const setData = activeTab === 'receptionist' ? setReceptionistData : setDoctorData;

        const re = parseFloat(data.pd_re) || 0;
        const le = parseFloat(data.pd_le) || 0;
        if (re || le) {
            setData(prev => ({ ...prev, pd_total: re + le }));
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            if (e.target.tagName === 'TEXTAREA' || e.target.tagName === 'BUTTON') return;
            
            e.preventDefault();
            const form = e.currentTarget;
            const elements = Array.from(form.elements).filter(el => 
                !el.disabled && 
                el.offsetParent !== null && 
                el.tabIndex !== -1
            );
            
            const index = elements.indexOf(e.target);
            if (index > -1 && index < elements.length - 1) {
                elements[index + 1].focus();
            }
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const payload = {
                // We update only the field corresponding to the active tab
                [activeTab === 'receptionist' ? 'receptionist_records' : 'doctor_records']:
                    activeTab === 'receptionist' ? receptionistData : doctorData,
                status: activeTab === 'receptionist' ? 'pre_test_done' : 'checkup_completed',
                ...(activeTab === 'receptionist' && {
                    amount_paid: parseFloat(String(amountPaid).replace(/,/g, '')) || 0,
                    payment_status: paymentStatus,
                    payment_method: paymentMethod
                })
            };
            
            // Also preserve the other record if needed, but PUT /appointments/:id updates fields passed.
            // If I pass only receptionist_records, Laravel update() updates only that column.

            const response = await ApiService.request({
                method: 'PUT',
                url: `admin/appointments/${id}`,
                data: payload
            });

            if (response.data.status) {
                toast.success(`${activeTab === 'receptionist' ? 'Receptionist' : 'Doctor'} records saved successfully`);
                // Redirect if receptionist
                if (activeTab === 'receptionist') {
                    navigate('/appointments');
                } else {
                    // Update local state to reflect saved data (though it's already there)
                    fetchData(); 
                }
            } else {
                toast.error(response.data.message);
            }
        } catch (error) {
            toast.error(error.response?.data?.message || "Failed to save records");
        } finally {
            setLoading(false);
        }
    };

    if (loading && !appointment) return <Loader />;
    if (!appointment) return <Loader />;

    const getLastCheckupData = () => {
        if (!lastCheckup) return {};
        // Prefer doctor records, fallback to receptionist records
        return lastCheckup.doctor_records || lastCheckup.receptionist_records || {};
    };

    const currentData = activeTab === 'receptionist' ? receptionistData : 
                        activeTab === 'doctor' ? doctorData : 
                        getLastCheckupData();

    const isReadOnly = activeTab === 'last_checkup';

    const renderTableView = () => (
        <div className="card-body border-top p-9">
            <div className="row mb-6">
                <label className="col-lg-4 fw-semibold fs-6 text-muted">Payment Method</label>
                <div className="col-lg-8">
                    <span className="fw-bold fs-6 text-gray-800">
                        {activeTab === 'last_checkup' && lastCheckup?.payment_method 
                            ? lastCheckup.payment_method 
                            : (paymentMethod || appointment?.payment_method || '-')}
                    </span>
                </div>
            </div>

            <div className="row mb-6">
                <label className="col-lg-4 fw-semibold fs-6 text-muted">Checkup Type</label>
                <div className="col-lg-8">
                    <span className="fw-bold fs-6 text-gray-800">{appointment?.checkup_type?.title}</span>
                </div>
            </div>

            {activeTab === 'receptionist' && (
                <>
                    <div className="separator separator-dashed my-5"></div>
                    <h5 className="mb-5 text-gray-700">Payment Details</h5>
                    <div className="row g-4 mb-6">
                        <div className="col-lg-3">
                            <div className="border rounded p-4 text-center bg-light-primary">
                                <div className="text-muted fs-7 mb-1">Total Fee</div>
                                <div className="fs-5 fw-bold text-gray-800">{formatCurrency(appointment?.price || 0)}</div>
                            </div>
                        </div>
                        <div className="col-lg-3">
                            <div className="border rounded p-4 text-center bg-light-success">
                                <div className="text-muted fs-7 mb-1">Amount Paid</div>
                                <div className="fs-5 fw-bold text-success">{formatCurrency(appointment?.amount_paid || 0)}</div>
                            </div>
                        </div>
                        <div className="col-lg-3">
                            <div className="border rounded p-4 text-center bg-light-danger">
                                <div className="text-muted fs-7 mb-1">Balance Due</div>
                                <div className="fs-5 fw-bold text-danger">{formatCurrency(Math.max(0, (parseFloat(appointment?.price || 0) - parseFloat(appointment?.amount_paid || 0))))}</div>
                            </div>
                        </div>
                        <div className="col-lg-3">
                            <div className="border rounded p-4 text-center bg-light-warning">
                                <div className="text-muted fs-7 mb-1">Payment Status</div>
                                <span className={`badge fw-bold fs-7 ${appointment?.payment_status === 'paid' ? 'badge-light-success' : appointment?.payment_status === 'partially_paid' ? 'badge-light-warning' : 'badge-light-danger'}`}>
                                    {appointment?.payment_status ? appointment.payment_status.replace('_', ' ').toUpperCase() : 'PENDING'}
                                </span>
                            </div>
                        </div>
                    </div>
                    <div className="separator separator-dashed my-5"></div>
                </>
            )}

            {activeTab === 'receptionist' && (
                <>
                    <h5 className="mb-3 text-muted">Auto Refraction (A/R)</h5>
                    <div className="table-responsive mb-6">
                        <table className="table table-bordered align-middle">
                            <thead>
                                <tr className="fw-bold fs-6 text-gray-800 bg-light">
                                    <th>Eye</th>
                                    <th>Value</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td className="fw-bold bg-light">Right Eye (R.E)</td>
                                    <td>{currentData.ar_re || '-'}</td>
                                </tr>
                                <tr>
                                    <td className="fw-bold bg-light">Left Eye (L.E)</td>
                                    <td>{currentData.ar_le || '-'}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </>
            )}

            <h5 className="mb-3 text-muted">{activeTab === 'receptionist' ? 'Present' : 'Final'} Rx (Lenso)</h5>
            <div className="table-responsive mb-6">
                <table className="table table-bordered align-middle">
                    <thead>
                        <tr className="fw-bold fs-6 text-gray-800 bg-light">
                            <th>Eye</th>
                            <th>SPH</th>
                            <th>CYL</th>
                            <th>AXIS</th>
                            <th>ADD</th>
                            <th>VA</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td className="fw-bold">R.E</td>
                            <td>{currentData.rx_re_sph || '-'}</td>
                            <td>{currentData.rx_re_cyl || '-'}</td>
                            <td>{currentData.rx_re_axis || '-'}</td>
                            <td>{currentData.rx_re_add || '-'}</td>
                            <td>{currentData.rx_re_va || '-'}</td>
                        </tr>
                        <tr>
                            <td className="fw-bold">L.E</td>
                            <td>{currentData.rx_le_sph || '-'}</td>
                            <td>{currentData.rx_le_cyl || '-'}</td>
                            <td>{currentData.rx_le_axis || '-'}</td>
                            <td>{currentData.rx_le_add || '-'}</td>
                            <td>{currentData.rx_le_va || '-'}</td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <h5 className="mb-3 text-muted">Pupillary Distance (PD)</h5>
            <div className="table-responsive mb-6">
                <table className="table table-bordered align-middle">
                    <thead>
                         <tr className="fw-bold fs-6 text-gray-800 bg-light">
                            <th>R.E</th>
                            <th>L.E</th>
                            <th>Total</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td>{currentData.pd_re || '-'}</td>
                            <td>{currentData.pd_le || '-'}</td>
                            <td>{currentData.pd_total || '-'}</td>
                        </tr>
                    </tbody>
                </table>
            </div>
            
            {(appointment?.checkup_type?.title === 'Full Eye Checkup' || appointment?.checkup_type?.title === 'IOP') && (
                <>
                    <h5 className="mb-3 text-muted">Intraocular Pressure (IOP)</h5>
                    <div className="table-responsive mb-6">
                        <table className="table table-bordered align-middle">
                            <thead>
                                <tr className="fw-bold fs-6 text-gray-800 bg-light">
                                    <th>R.E (mmHg)</th>
                                    <th>L.E (mmHg)</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td>{currentData.iop_re || '-'}</td>
                                    <td>{currentData.iop_le || '-'}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </>
            )}

            <div className="row mb-6">
                <label className="col-lg-4 fw-semibold fs-6 text-muted">Lens Types</label>
                <div className="col-lg-8">
                    <span className="fw-bold fs-6 text-gray-800">
                        {currentData.lens_types && currentData.lens_types.length > 0 
                            ? currentData.lens_types.map(l => l.name).join(', ') 
                            : '-'}
                    </span>
                </div>
            </div>

            {(activeTab === 'doctor' || activeTab === 'last_checkup') && (
                <>
                    <div className="separator separator-dashed my-5"></div>
                    <div className="row mb-6">
                        <label className="col-lg-4 fw-semibold fs-6 text-muted">Findings</label>
                        <div className="col-lg-8">
                            <span className="fw-bold fs-6 text-gray-800">{currentData.doctor_findings || '-'}</span>
                        </div>
                    </div>
                    <div className="row mb-6">
                        <label className="col-lg-4 fw-semibold fs-6 text-muted">Medications</label>
                        <div className="col-lg-8">
                            <span className="fw-bold fs-6 text-gray-800">{currentData.doctor_medications || '-'}</span>
                        </div>
                    </div>
                    <div className="row mb-6">
                        <label className="col-lg-4 fw-semibold fs-6 text-muted">Specialist Recommendation</label>
                        <div className="col-lg-8">
                            <span className="fw-bold fs-6 text-gray-800">{currentData.specialist_recommendation || '-'}</span>
                        </div>
                    </div>
                     <div className="row mb-6">
                        <label className="col-lg-4 fw-semibold fs-6 text-muted">Next Visit</label>
                        <div className="col-lg-8">
                            <span className="fw-bold fs-6 text-gray-800">{currentData.next_visit || '-'}</span>
                        </div>
                    </div>
                </>
            )}
            
            <div className="card-footer d-flex justify-content-end py-6 px-9">
                 <button type="button" className="btn btn-light btn-active-light-primary me-2" onClick={() => navigate('/appointments')}>Back</button>
                 {activeTab !== 'last_checkup' && ['receptionist', 'admin'].includes(authUser.role) && (
                    <button type="button" className="btn btn-primary" onClick={() => setIsEditMode(true)}>Edit Records</button>
                 )}
            </div>
        </div>
    );

    return (
        <div className="d-flex flex-column flex-column-fluid">
            <div id="kt_app_toolbar" className="app-toolbar py-3 py-lg-6">
                <div id="kt_app_toolbar_container" className="app-container container-fluid d-flex flex-stack">
                    <div className="page-title d-flex flex-column justify-content-center flex-wrap me-3">
                        <h1 className="page-heading d-flex text-dark fw-bold fs-3 flex-column justify-content-center my-0">
                            Checkup Details (Ref: {appointment?.ref_no})
                        </h1>
                        {appointment && (
                             <ul className="breadcrumb breadcrumb-separatorless fw-semibold fs-7 my-0 pt-1">
                                <li className="breadcrumb-item text-muted">Patient: {appointment.patient?.name}</li>
                                <li className="breadcrumb-item"><span className="bullet bg-gray-400 w-5px h-2px"></span></li>
                                <li className="breadcrumb-item text-muted">Token: {appointment.token_no}</li>
                            </ul>
                        )}
                    </div>

                <span className={`badge fs-5 ${getAppointmentStatus(appointment?.status).badgeClass}`}>
                    {getAppointmentStatus(appointment?.status).label}
                </span>
                </div>
            </div>

            <div id="kt_app_content" className="app-content flex-column-fluid">
                <div id="kt_app_content_container" className="app-container container-fluid">
                    
                    {/* Patient Info Header */}
                    <div className="card mb-5 mb-xl-10" style={{ position: 'sticky', top: '0', zIndex: 99 }}>
                        <div className="card-body pt-9 pb-0">
                            <div className="d-flex flex-wrap flex-sm-nowrap mb-3">
                                <div className="flex-grow-1">
                                    <div className="d-flex justify-content-between align-items-start flex-wrap mb-2">
                                        <div className="d-flex flex-column">
                                            <div className="d-flex align-items-center mb-2">
                                                <span className="text-gray-900 text-hover-primary fs-2 fw-bold me-1">
                                                    {appointment.patient?.name}
                                                </span>
                                                <span className={`badge ${getAppointmentStatus(appointment?.status).badgeClass} fw-bold ms-2 fs-8 py-1 px-3`}>
                                                    {getAppointmentStatus(appointment?.status).label}
                                                </span>
                                            </div>
                                            <div className="d-flex flex-wrap fw-semibold fs-6 mb-4 pe-2">
                                                <span className="d-flex align-items-center text-gray-400 me-5 mb-2">
                                                    <i className="fas fa-phone me-1"></i> {appointment.patient?.phone}
                                                </span>
                                                <span className="d-flex align-items-center text-gray-400 me-5 mb-2">
                                                    <i className="fas fa-venus-mars me-1"></i> {appointment.patient?.gender || 'N/A'}
                                                </span>
                                                <span className="d-flex align-items-center text-gray-400 me-5 mb-2">
                                                    <i className="fas fa-birthday-cake me-1"></i> {appointment.patient?.date_of_birth ? `${calculateAge(appointment.patient.date_of_birth)} Yrs` : 'N/A'}
                                                </span>
                                                <span className="d-flex align-items-center text-gray-400 mb-2">
                                                    <i className="fas fa-credit-card me-1"></i> {appointment.payment_method ? appointment.payment_method.charAt(0).toUpperCase() + appointment.payment_method.slice(1) : 'N/A'}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="card mb-5 mb-xl-10">
                        <div className="card-header border-0 cursor-pointer">
                            <div className="card-title m-0">
                                <ul className="nav nav-stretch nav-line-tabs nav-line-tabs-2x border-transparent fs-5 fw-bold">
                                    <li className="nav-item mt-2">
                                        <button 
                                            className={`nav-link text-active-primary ms-0 me-10 py-5 ${activeTab === 'receptionist' ? 'active' : ''}`} 
                                            onClick={() => setActiveTab('receptionist')}
                                        >
                                            Receptionist
                                        </button>
                                    </li>
                                    <li className="nav-item mt-2">
                                        <button 
                                            className={`nav-link text-active-primary ms-0 me-10 py-5 ${activeTab === 'doctor' ? 'active' : ''}`} 
                                            onClick={() => setActiveTab('doctor')}
                                        >
                                            Doctor
                                        </button>
                                    </li>
                                    <li className="nav-item mt-2">
                                        <button 
                                            className={`nav-link text-active-primary ms-0 me-10 py-5 ${activeTab === 'last_checkup' ? 'active' : ''}`} 
                                            onClick={() => setActiveTab('last_checkup')}
                                        >
                                            Last Checkup
                                        </button>
                                    </li>
                                </ul>
                            </div>
                        </div>
                        
                        <div className="collapse show">
                            {isEditMode ? (
                            <form onSubmit={handleSubmit} onKeyDown={handleKeyDown}>
                                <div className="card-body border-top p-9">
                                    
                                    <div className="row mb-6">
                                        <label className="col-lg-4 col-form-label fw-semibold fs-6">Payment Method</label>
                                        <div className="col-lg-8 fv-row">
                                            <select
                                                className="form-select form-select-solid form-select-lg"
                                                value={paymentMethod}
                                                onChange={(e) => setPaymentMethod(e.target.value)}
                                                disabled={activeTab !== 'receptionist'}
                                            >
                                                <option value="">Select Method</option>
                                                <option value="cash">Cash</option>
                                                <option value="card">Card</option>
                                                <option value="mobile_money">Mobile Money</option>
                                                <option value="insurance">Insurance</option>
                                            </select>
                                        </div>
                                    </div>

                                    <div className="row mb-6">
                                        <label className="col-lg-4 col-form-label  fw-semibold fs-6">Checkup Type</label>
                                        <div className="col-lg-8 fv-row">
                                            <input type="text" className='form-control' disabled name="checkup_type_id" value={appointment.checkup_type?.title ?? '--'} />
                                        </div>
                                    </div>

                                    {activeTab === 'receptionist' && (
                                        <>
                                            <div className="separator separator-dashed my-5"></div>
                                            <h5 className="mb-5 text-gray-700">Payment Details</h5>

                                            <div className="row g-4 mb-6">
                                                <div className="col-lg-3">
                                                    <div className="border rounded p-4 text-center bg-light-primary">
                                                        <div className="text-muted fs-7 mb-1">Total Fee</div>
                                                        <div className="fs-5 fw-bold text-gray-800">{formatCurrency(appointment?.price || 0)}</div>
                                                    </div>
                                                </div>
                                                <div className="col-lg-3">
                                                    <div className="border rounded p-4 text-center bg-light-success">
                                                        <div className="text-muted fs-7 mb-1">Amount Paid</div>
                                                        <div className="fs-5 fw-bold text-success">{formatCurrency(parseFloat(String(amountPaid).replace(/,/g, '')) || 0)}</div>
                                                    </div>
                                                </div>
                                                <div className="col-lg-3">
                                                    <div className="border rounded p-4 text-center bg-light-danger">
                                                        <div className="text-muted fs-7 mb-1">Balance Due</div>
                                                        <div className="fs-5 fw-bold text-danger">{formatCurrency(Math.max(0, (parseFloat(appointment?.price || 0) - (parseFloat(String(amountPaid).replace(/,/g, '')) || 0))))}</div>
                                                    </div>
                                                </div>
                                                <div className="col-lg-3">
                                                    <div className="border rounded p-4 text-center bg-light-warning">
                                                        <div className="text-muted fs-7 mb-1">Payment Status</div>
                                                        <span className={`badge fw-bold fs-7 ${paymentStatus === 'paid' ? 'badge-light-success' : paymentStatus === 'partially_paid' ? 'badge-light-warning' : 'badge-light-danger'}`}>
                                                            {paymentStatus ? paymentStatus.replace('_', ' ').toUpperCase() : 'PENDING'}
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="row mb-6">
                                                <div className="col-lg-6">
                                                    <label className="form-label fw-semibold fs-6">Amount Paid</label>
                                                    <input
                                                        type="text"
                                                        className="form-control form-control-lg form-control-solid"
                                                        value={formatNumberWithCommas(amountPaid)}
                                                        onChange={(e) => {
                                                            const raw = e.target.value.replace(/,/g, '');
                                                            if (!/^\d*\.?\d*$/.test(raw)) return;
                                                            setAmountPaid(raw);
                                                            const paid = parseFloat(raw) || 0;
                                                            const total = parseFloat(appointment?.price || 0);
                                                            if (paid >= total && total > 0) setPaymentStatus('paid');
                                                            else if (paid > 0) setPaymentStatus('partially_paid');
                                                            else setPaymentStatus('pending');
                                                        }}
                                                        placeholder="0.00"
                                                    />
                                                </div>
                                                <div className="col-lg-6">
                                                    <label className="form-label fw-semibold fs-6">Payment Status</label>
                                                    <select
                                                        className="form-select form-select-solid form-select-lg"
                                                        value={paymentStatus}
                                                        onChange={(e) => setPaymentStatus(e.target.value)}
                                                    >
                                                        <option value="pending">Pending</option>
                                                        <option value="partially_paid">Partially Paid</option>
                                                        <option value="paid">Paid</option>
                                                    </select>
                                                </div>
                                            </div>
                                            <div className="separator separator-dashed my-5"></div>
                                        </>
                                    )}

                                    {activeTab === 'receptionist' && appointment?.checkup_type?.title !== 'IOP' && (
                                        <>
                                            <h5 className="mb-3">Auto Refraction (A/R)</h5>
                                            <div className="row mb-6">
                                                <div className="col-lg-6">
                                                    <label className="form-label fw-semibold fs-6">Right Eye (R.E)</label>
                                                    <input type="text" className="form-control form-control-lg form-control-solid" name="ar_re" value={currentData.ar_re || ''} onChange={handleChange} placeholder="Sph Cyl Axis" disabled={isReadOnly} />
                                                </div>
                                                <div className="col-lg-6">
                                                    <label className="form-label fw-semibold fs-6">Left Eye (L.E)</label>
                                                    <input type="text" className="form-control form-control-lg form-control-solid" name="ar_le" value={currentData.ar_le || ''} onChange={handleChange} placeholder="Sph Cyl Axis" disabled={isReadOnly} />
                                                </div>
                                            </div>
                                        </>
                                    )}

                                    {appointment?.checkup_type?.title !== 'IOP' && (
                                    <>
                                    <h5 className="mb-3">{activeTab === 'receptionist' ? 'Present' : 'Final'} Rx (Lenso)</h5>
                                    <div className="table-responsive mb-6">
                                        <table className="table table-bordered align-middle">
                                            <thead>
                                                <tr className="fw-bold fs-6 text-gray-800 bg-light">
                                                    <th>Eye</th>
                                                    <th>SPH</th>
                                                    <th>CYL</th>
                                                    <th>AXIS</th>
                                                    <th>ADD</th>
                                                    <th>VA</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                <tr>
                                                    <td className="fw-bold">R.E</td>
                                                    <td><input type="text" className="form-control form-control-solid" name="rx_re_sph" value={currentData.rx_re_sph || ''} onChange={handleChange} disabled={isReadOnly} /></td>
                                                    <td><input type="text" className="form-control form-control-solid" name="rx_re_cyl" value={currentData.rx_re_cyl || ''} onChange={handleChange} disabled={isReadOnly} /></td>
                                                    <td><input type="text" className="form-control form-control-solid" name="rx_re_axis" value={currentData.rx_re_axis || ''} onChange={handleChange} disabled={isReadOnly} /></td>
                                                    <td><input type="text" className="form-control form-control-solid" name="rx_re_add" value={currentData.rx_re_add || ''} onChange={handleChange} disabled={isReadOnly} /></td>
                                                    <td><input type="text" className="form-control form-control-solid" name="rx_re_va" value={currentData.rx_re_va || ''} onChange={handleChange} disabled={isReadOnly} /></td>
                                                </tr>
                                                <tr>
                                                    <td className="fw-bold">L.E</td>
                                                    <td><input type="text" className="form-control form-control-solid" name="rx_le_sph" value={currentData.rx_le_sph || ''} onChange={handleChange} disabled={isReadOnly} /></td>
                                                    <td><input type="text" className="form-control form-control-solid" name="rx_le_cyl" value={currentData.rx_le_cyl || ''} onChange={handleChange} disabled={isReadOnly} /></td>
                                                    <td><input type="text" className="form-control form-control-solid" name="rx_le_axis" value={currentData.rx_le_axis || ''} onChange={handleChange} disabled={isReadOnly} /></td>
                                                    <td><input type="text" className="form-control form-control-solid" name="rx_le_add" value={currentData.rx_le_add || ''} onChange={handleChange} disabled={isReadOnly} /></td>
                                                    <td><input type="text" className="form-control form-control-solid" name="rx_le_va" value={currentData.rx_le_va || ''} onChange={handleChange} disabled={isReadOnly} /></td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>

                                    <h5 className="mb-3">Pupillary Distance (PD)</h5>
                                    <div className="row mb-6">
                                        <div className="col-lg-4">
                                            <label className="form-label fw-semibold fs-6">R.E</label>
                                            <input type="number" step="0.1" className="form-control form-control-lg form-control-solid" name="pd_re" value={currentData.pd_re || ''} onChange={handleChange} onBlur={calculateTotalPD} disabled={isReadOnly} />
                                        </div>
                                        <div className="col-lg-4">
                                            <label className="form-label fw-semibold fs-6">L.E</label>
                                            <input type="number" step="0.1" className="form-control form-control-lg form-control-solid" name="pd_le" value={currentData.pd_le || ''} onChange={handleChange} onBlur={calculateTotalPD} disabled={isReadOnly} />
                                        </div>
                                        <div className="col-lg-4">
                                            <label className="form-label fw-semibold fs-6">Total</label>
                                            <input type="number" step="0.1" className="form-control form-control-lg form-control-solid" name="pd_total" value={currentData.pd_total || ''} onChange={handleChange} disabled={isReadOnly} />
                                        </div>
                                    </div>
                                    </>
                                    )}

                                    {/* {activeTab === 'receptionist' && appointment?.checkup_type?.title !== 'IOP' && (
                                        <>
                                            <h5 className="mb-3">ADD</h5>
                                            <div className="row mb-6">
                                                <div className="col-lg-6">
                                                    <label className="form-label fw-semibold fs-6">R.E</label>
                                                    <input type="text" className="form-control form-control-lg form-control-solid" name="lensometer_re" value={currentData.lensometer_re || ''} onChange={handleChange} disabled={isReadOnly} />
                                                </div>
                                                <div className="col-lg-6">
                                                    <label className="form-label fw-semibold fs-6">L.E</label>
                                                    <input type="text" className="form-control form-control-lg form-control-solid" name="lensometer_le" value={currentData.lensometer_le || ''} onChange={handleChange} disabled={isReadOnly} />
                                                </div>
                                            </div>
                                        </>
                                    )} */}

                                    {(appointment?.checkup_type?.title === 'Full Eye Checkup' || appointment?.checkup_type?.title === 'IOP') && (
                                        <>
                                            <h5 className="mb-3">Intraocular Pressure (IOP)</h5>
                                            <div className="row mb-6">
                                                <div className="col-lg-6">
                                                    <label className="form-label fw-semibold fs-6">R.E (mmHg)</label>
                                                    <input type="text" className="form-control form-control-lg form-control-solid" name="iop_re" value={currentData.iop_re || ''} onChange={handleChange} disabled={isReadOnly} />
                                                </div>
                                                <div className="col-lg-6">
                                                    <label className="form-label fw-semibold fs-6">L.E (mmHg)</label>
                                                    <input type="text" className="form-control form-control-lg form-control-solid" name="iop_le" value={currentData.iop_le || ''} onChange={handleChange} disabled={isReadOnly} />
                                                </div>
                                            </div>
                                        </>
                                    )}

                                    {appointment?.checkup_type?.title !== 'IOP' && (
                                    <>
                                    <div className="d-flex align-items-center justify-content-between mb-3">
                                        <h5 className="mb-0">Lens Types</h5>
                                        <a
                                            href="/lens-types/create"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="btn btn-sm btn-light-primary"
                                        >
                                            <i className="fas fa-plus me-1"></i> Add New
                                        </a>
                                    </div>
                                    <div className="row mb-6">
                                        <div className="col-lg-12">
                                            <MultiSelect
                                                value={currentData.lens_types || []}
                                                options={lensTypes}
                                                onChange={(e) => {
                                                    if (activeTab === 'receptionist') {
                                                        setReceptionistData(prev => ({ ...prev, lens_types: e.value }));
                                                    } else {
                                                        setDoctorData(prev => ({ ...prev, lens_types: e.value }));
                                                    }
                                                }}
                                                onShow={getLensTypes}
                                                optionLabel="name"
                                                dataKey="id"
                                                placeholder="Select Lens Types"
                                                display="chip"
                                                className="w-100"
                                                filter
                                                disabled={isReadOnly}
                                            />
                                        </div>
                                    </div>
                                    </>
                                    )}

                                    <div className="separator separator-dashed my-5"></div>

                                    {(activeTab === 'doctor' || activeTab === 'last_checkup') && appointment?.checkup_type?.title !== 'IOP' && (
                                        <>
                                            <div className="row mb-6">
                                                <label className="col-lg-4 col-form-label fw-semibold fs-6">Findings</label>
                                                <div className="col-lg-8 fv-row">
                                                    <div className="position-relative">
                                                        <textarea 
                                                            className="form-control form-control-solid" 
                                                            rows="4" 
                                                            name="doctor_findings" 
                                                            value={currentData.doctor_findings || ''} 
                                                            onChange={handleChange}
                                                            disabled={isReadOnly}
                                                        ></textarea>
                                                        {currentData.doctor_findings && !isReadOnly && (
                                                            <button 
                                                                type="button" 
                                                                className="btn btn-sm btn-icon btn-active-color-danger position-absolute top-0 end-0 m-2"
                                                                onClick={() => {
                                                                    if (activeTab === 'receptionist') {
                                                                        setReceptionistData(prev => ({ ...prev, doctor_findings: '' }))
                                                                    } else {
                                                                        setDoctorData(prev => ({ ...prev, doctor_findings: '' }))
                                                                    }
                                                                }}
                                                                title="Clear Findings"
                                                            >
                                                                <i className="fas fa-times"></i>
                                                            </button>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="row mb-6">
                                                <label className="col-lg-4 col-form-label fw-semibold fs-6">Medications</label>
                                                <div className="col-lg-8 fv-row">
                                                    <div className="position-relative">
                                                        <textarea 
                                                            className="form-control form-control-solid" 
                                                            rows="4" 
                                                            name="doctor_medications" 
                                                            value={currentData.doctor_medications || ''} 
                                                            onChange={handleChange}
                                                            disabled={isReadOnly}
                                                        ></textarea>
                                                        {currentData.doctor_medications && !isReadOnly && (
                                                            <button 
                                                                type="button" 
                                                                className="btn btn-sm btn-icon btn-active-color-danger position-absolute top-0 end-0 m-2"
                                                                onClick={() => {
                                                                    if (activeTab === 'receptionist') {
                                                                        setReceptionistData(prev => ({ ...prev, doctor_medications: '' }))
                                                                    } else {
                                                                        setDoctorData(prev => ({ ...prev, doctor_medications: '' }))
                                                                    }
                                                                }}
                                                                title="Clear Medications"
                                                            >
                                                                <i className="fas fa-times"></i>
                                                            </button>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="row mb-6">
                                                <label className="col-lg-4 col-form-label fw-semibold fs-6">Recommend Specialist</label>
                                                <div className="col-lg-8 fv-row d-flex align-items-center">
                                                    <div className="form-check form-check-custom form-check-solid">
                                                        <input 
                                                            className="form-check-input" 
                                                            type="checkbox" 
                                                            name="specialist_recommendation"
                                                            checked={currentData.specialist_recommendation === 'Yes'} 
                                                            onChange={(e) => {
                                                                const val = e.target.checked ? 'Yes' : 'No';
                                                                if (activeTab === 'receptionist') {
                                                                    setReceptionistData(prev => ({ ...prev, specialist_recommendation: val }));
                                                                } else {
                                                                    setDoctorData(prev => ({ ...prev, specialist_recommendation: val }));
                                                                }
                                                            }}
                                                            disabled={isReadOnly}
                                                        />
                                                        <label className="form-check-label fw-semibold text-gray-700">
                                                            Yes, recommend a specialist
                                                        </label>
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="row mb-6">
                                                <label className="col-lg-4 col-form-label fw-semibold fs-6">Next Visit</label>
                                                <div className="col-lg-8 fv-row">
                                                    <input 
                                                        type="month" 
                                                        className="form-control form-control-lg form-control-solid" 
                                                        name="next_visit" 
                                                        value={currentData.next_visit || ''} 
                                                        onChange={handleChange} 
                                                        disabled={isReadOnly}
                                                    />
                                                    <div className="form-text">Month and Year</div>
                                                </div>
                                            </div>
                                        </>
                                    )}

                                </div>
                                <div className="card-footer d-flex justify-content-end py-6 px-9">
                                    <button type="button" className="btn btn-light btn-active-light-primary me-2" onClick={() => navigate('/appointments')}>Back</button>
                                    {!isReadOnly && (
                                        <button type="submit" className="btn btn-base" disabled={loading}>
                                            {!loading ? `Save ${activeTab === 'receptionist' ? 'Receptionist' : 'Doctor'} Records` : 'Saving...'}
                                        </button>
                                    )}
                                </div>
                            </form>
                            ) : (
                                renderTableView()
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Checkup;